import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useHookstate } from '@hookstate/core';
import { Auth, I18n } from 'aws-amplify';
import classNames from 'classnames';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import { BlockUI } from 'primereact/blockui';
import 'primereact/resources/primereact.css';
import "primereact/resources/themes/lara-light-blue/theme.css";
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import CookieConsent from "react-cookie-consent";
import { Route } from "react-router-dom";
import { CSSTransition } from 'react-transition-group';
import { globalBlockedState, globalIsAdminState, globalShowCguState, ToastContext } from './app-context';
import './App.scss';
import { AppFooter } from './AppFooter';
import { AppMenu } from './AppMenu';
import { AppTopbar } from './AppTopbar';
import Dashboard from './components/Dashboard';
import { Profile } from './components/manage/Profile';
import ScrbMain from './components/scrb/ScrbMain';
import { currentCoffret, currentInstallation, currentTournee, scrbData } from './components/scrb/ScrbTools';
import { StructureSwitcher } from './components/StructureSwitcher';
import './layout/flags/flags.css';
import './layout/layout.scss';
import { EvaluationPage } from './pages/apprentissage/EvaluationPage';
import { getAllStructures } from './service/CdmGlobalService';
import { getScrbData } from './service/ScrbService';
import { MyStructuresContext, StructureContext } from './structure-context';
import { globalUserState, isGroupMembersWithoutHook } from './user-context';
import { components, formFields, getCgu } from './Utils';
import Cgu from './Cgu';

const App = () => {
    const blockedState = useHookstate(globalBlockedState);
    const isAdmin = useHookstate(globalIsAdminState);
    const [layoutMode] = useState('static');
    const [layoutColorMode] = useState('light')
    const [inputStyle] = useState('outlined');
    const [scale] = useState(14);
    const [ripple] = useState(true);
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
    const [user, setUser] = useState();
    const [structure, setStructure] = useState({});
    const [structures, setStructures] = useState([]);
    const [apprentissage, setApprentissage] = useState(false);
    const [scrb, setScrb] = useState(false);
    const data = useHookstate(scrbData);
    const { route } = useAuthenticator(context => [context.route]);
    const toast = useRef(null);
    const [cgu, setCgu] = useState([]);
    const showCguState = useHookstate(globalShowCguState);
    const showCgu = showCguState.get();

    const toggleStructure = (s) => {
        setStructure(s);
        blockedState.set(false);
        if(user) {
            checkCguAccepted(user[`cdm-groups:${s.id}`].split(' '), user[`cguAccepted:${s.id}`]);
        }
    }

    useEffect(() => {
        setApprentissage(isGroupMembersWithoutHook(['gerants', 'superviseurs_apprentissage'], structure, user));
        setScrb(isGroupMembersWithoutHook(['scrb_gaz', 'scrb_clim', 'scrb_mazout', 'scrb_solide', 'scrb_pompe_chaleur'], structure, user));
        isAdmin.set(isGroupMembersWithoutHook(['observateurs'], structure, user));
        //eslint-disable-next-line
    }, [structure, user]);

    let menuClick = false;
    let mobileTopbarMenuClick = false;

    useEffect(() => {
        document.documentElement.style.fontSize = scale + 'px';
    }, [scale]);

    useEffect(() => {
        if (route === 'authenticated') {
            Auth.currentAuthenticatedUser({ bypassCache: true }).then(() => Auth.currentSession()).then(res => {
                const payloadIdToken = res.getIdToken().payload;
                setUser(payloadIdToken);
                if (payloadIdToken.locale) {
                    I18n.setLanguage(payloadIdToken.locale.toLowerCase());
                    localStorage.setItem("i18nextLng", payloadIdToken.locale.toLowerCase());
                }
                globalUserState.set(payloadIdToken);
                const myStructures = Object.entries(payloadIdToken).filter(e => e[0].startsWith('cdm-structure:')).map(fe => {
                    return {
                        id: fe[0].substring(14),
                        name: fe[1],
                    };
                }).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
                setStructures(myStructures);
                const structureId = myStructures.find(e => JSON.stringify(e.id) === localStorage.getItem("structureId"));
                if (structureId != null) {
                    setStructure(structureId);
                    checkCguAccepted(payloadIdToken[`cdm-groups:${structureId.id}`].split(' '), payloadIdToken[`cguAccepted:${structureId.id}`]);
                } else {
                    localStorage.removeItem("structureId");
                    setStructure(myStructures[0]);
                    checkCguAccepted(payloadIdToken[`cdm-groups:${myStructures[0].id}`].split(' '), payloadIdToken[`cguAccepted:${myStructures[0].id}`]);
                }
                if (payloadIdToken["cdm-structures-truncated"] === "true") {
                    loadAllStructures(payloadIdToken);
                }
            }).catch(err => {
                console.error("Erreur lors de la récupération de la session:", err);
            });
        }
        //eslint-disable-next-line
    }, [route]);

    const checkCguAccepted = (groups, cguAccepted) => {
        const nextCgu = getCgu(groups, cguAccepted);
        setCgu(nextCgu);
        showCguState.set(nextCgu.length > 0);
    }

    const loadAllStructures = (payload) => {
        getAllStructures((structures) => {
            setUser(buildPayload(structures, payload));
            setStructures(buildStructures(structures));
            setStructure({ id: structures.structures[0].structureId, name: structures.structures[0].structureName });
        });
    }

    const buildPayload = (structures, payloadIdToken) => {
        structures.structures.forEach(structure => {
            payloadIdToken[`cdm-groups:${structure.structureId}`] = structure.roles.join(" ");
            payloadIdToken[`cdm-structure:${structure.structureId}`] = structure.structureName;
        });
        return payloadIdToken;
    }

    const buildStructures = (structures) => {
        return structures.structures.map(structure => {
            return {
                id: structure.structureId,
                name: structure.structureName,
            };
        });
    }

    useEffect(() => {
        const script = document.createElement('script');

        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        }
    }, []);

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, "body-overflow-hidden");
        } else {
            removeClass(document.body, "body-overflow-hidden");
        }
    }, [mobileMenuActive]);

    useEffect(() => {
        if (scrb) {
            getScrbData().then(res => {
                data.set(res);
            });
        }
        //eslint-disable-next-line
    }, [scrb]);

    const onWrapperClick = (event) => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false);
        }

        mobileTopbarMenuClick = false;
        menuClick = false;
    }

    const onToggleMenuClick = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                if (mobileMenuActive === true) {
                    setOverlayMenuActive(true);
                }

                setOverlayMenuActive((prevState) => !prevState);
                setMobileMenuActive(false);
            }
            else if (layoutMode === 'static') {
                setStaticMenuInactive((prevState) => !prevState);
            }
        }
        else {
            setMobileMenuActive((prevState) => !prevState);
        }

        event.preventDefault();
    }

    const onSidebarClick = () => {
        menuClick = true;
    }

    const onMobileTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;
        setMobileTopbarMenuActive((prevState) => !prevState);
        event.preventDefault();
    }

    const onMobileSubTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;
        event.preventDefault();
    }

    const onMenuItemClick = (event) => {
        currentInstallation.set({});
        currentTournee.set({});
        currentCoffret.set({});
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    }
    const isDesktop = () => {
        return window.innerWidth >= 992;
    }

    const addClass = (element, className) => {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    }

    const removeClass = (element, className) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-light': layoutColorMode === 'light'
    });

    return (<Authenticator hideSignUp={true} variation="modal" components={components} formFields={formFields}>
        <StructureContext.Provider value={structure}>
            <MyStructuresContext.Provider value={structures}>
                <ToastContext.Provider value={toast}>
                    <div className={wrapperClass} onClick={onWrapperClick}>
                        <AppTopbar onToggleMenuClick={onToggleMenuClick} layoutColorMode={layoutColorMode} showCgu={showCgu}
                            mobileTopbarMenuActive={mobileTopbarMenuActive} onMobileTopbarMenuClick={onMobileTopbarMenuClick} onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick} />
                        <div className="layout-sidebar" onClick={onSidebarClick}>
                            <StructureSwitcher toggleStructure={toggleStructure} />
                            <AppMenu onMenuItemClick={onMenuItemClick} layoutColorMode={layoutColorMode} />
                        </div>
                        <div className="layout-main-container">
                            <BlockUI blocked={blockedState.get()} fullScreen template={<i className="fcdm fcdm-m" style={{ 'fontSize': '3rem' }} />}></BlockUI>
                            <Toast ref={toast} className="cdm-toast" />
                            <div className="layout-main">
                                {((scrb && data.get()) || !scrb) && (<Route path="/" exact component={() => <Dashboard scrb={scrb} />} />)}
                                <Route path="/profile" exact component={Profile} />
                                {apprentissage && (<Route path="/evaluations" exact component={EvaluationPage} />)}
                                {scrb && (<Route path="/scrb/installations" exact component={ScrbMain} />)}
                                {scrb && (<Route path="/scrb/tournees" exact component={ScrbMain} />)}
                                {scrb && (<Route path="/scrb/coffrets" exact component={ScrbMain} />)}
                                {scrb && (<Route path="/scrb/informations" exact component={ScrbMain} />)}
                                {showCgu && cgu.length > 0 && <Cgu cguFiles={cgu} locale={localStorage.getItem("i18nextLng")} />}
                                <CookieConsent buttonStyle={{ margin: "15px", background: "#efb600", border: "0", boxShadow: "none", borderRadius: "0px", cursor: "pointer", flex: "0 0 auto", padding: "5px 10px" }} disableButtonStyles={true} buttonText={I18n.get("cookieaccept")}
                                >{I18n.get("cookieconsent")}</CookieConsent>
                            </div>
                            <AppFooter layoutColorMode={layoutColorMode} />
                        </div>
                        <CSSTransition classNames="layout-mask" timeout={{ enter: 200, exit: 200 }} in={mobileMenuActive} unmountOnExit>
                            <div className="layout-mask p-component-overlay"></div>
                        </CSSTransition>
                    </div>
                </ToastContext.Provider>
            </MyStructuresContext.Provider>
        </StructureContext.Provider>
    </Authenticator>
    );

}

export default App;
